import React from 'react';
import { Helmet } from 'react-helmet';

import { getDomain, getDomainBaseUrl } from './domains';
import { isDefined } from './is-defined';
import { useSiteMetadataQuery } from './use-SiteMetadataQuery';

type MetaHTMLType = React.DetailedHTMLProps<
  React.MetaHTMLAttributes<HTMLMetaElement>,
  HTMLMetaElement
>[];

type LinkHTMLType = React.DetailedHTMLProps<
  React.LinkHTMLAttributes<HTMLLinkElement>,
  HTMLLinkElement
>[];

type SEOProps = {
  title?: string | undefined;
  description?: string | undefined;
  lang?: string | undefined;
  meta?: MetaHTMLType;
  links?: LinkHTMLType;
};

const makeURLRelative = (input: any): string => {
  let relativePath = '';

  if (typeof input == 'string') {
    if (input.includes('/sites/')) {
      relativePath = input;
    } else {
      const newURL = new URL(input);
      relativePath = newURL.pathname;
    }
  } else if (typeof input == 'object') {
    const strInput = input[0].content;

    if (strInput.includes('https://') || strInput.includes('http://')) {
      const newURL = new URL(strInput);
      relativePath = newURL.pathname;
    } else {
      relativePath = input[0].content;
    }
  }

  return relativePath;
};

export const SEO: React.FC<SEOProps> = ({
  title,
  description = '',
  lang = 'de',
  meta = [],
  links = [],
}) => {
  const { site } = useSiteMetadataQuery();

  const metaDescription = description || site?.siteMetadata?.description;

  const metaOGImage = meta
    .filter(isDefined)
    .filter(({ property }) => property === 'og:image');

  const metaOGUrl = meta
    .filter(isDefined)
    .filter(({ property }) => property === 'og:url');

  const metaTwitterImage = meta
    .filter(isDefined)
    .filter(({ name }) => name === 'twitter:image');

  const metaTwitterUrl = meta
    .filter(isDefined)
    .filter(({ name }) => name === 'twitter:url');

  const metaImgOrFallback = (hasImg: any): string | undefined => {
    if (hasImg || hasImg.length) {
      if (hasImg[0] && hasImg[0].content) {
        return hasImg[0].content
          ? makeURLRelative(hasImg[0].content)
          : `/${getDomain()}-fallback-image.jpeg`;
      }
    } else {
      return `/${getDomain()}-fallback-image.jpeg`;
    }
  };

  // Meta without the following items, filtered out.
  const newMeta = meta.filter(isDefined).filter(
    ({ name, property }) =>
      property !== 'og:image' &&
      property !== 'og:url' &&
      name !== 'twitter:image' &&
      name !== 'twitter:url' &&
      property !== 'og:image:secure_url', // Removes unnecessary metatag.
  );

  // If Meta from Drupal
  if (meta || links) {
    return (
      <>
        <Helmet
          htmlAttributes={{
            lang,
          }}
          title={title}
          meta={[
            {
              name: `viewport`,
              content: site?.siteMetadata?.viewport,
            },
            {
              name: `generator`,
              content: site?.siteMetadata?.generator,
            },
            {
              name: `HandheldFriendly`,
              content: `true`,
            },
            {
              name: `MobileOptimized`,
              content: `width`,
            },
            ...newMeta,
            {
              property: `og:image`,
              content: metaOGImage.length
                ? getDomainBaseUrl() + metaImgOrFallback(metaOGImage)
                : getDomainBaseUrl() + `/${getDomain()}-fallback-image.jpeg`,
            },
            {
              property: `og:url`,
              content: metaOGUrl.length
                ? getDomainBaseUrl() + makeURLRelative(metaOGUrl)
                : getDomainBaseUrl(),
            },
            {
              name: `twitter:url`,
              content: metaTwitterUrl.length
                ? getDomainBaseUrl() + makeURLRelative(metaTwitterUrl)
                : getDomainBaseUrl(),
            },
            {
              name: `twitter:image`,
              content: metaTwitterImage.length
                ? getDomainBaseUrl() + metaImgOrFallback(metaTwitterImage)
                : getDomainBaseUrl() + `/${getDomain()}-fallback-image.jpeg`,
            },
          ]}
        />
        <Helmet>
          {links.map((link, index) => (
            <link
              key={index}
              rel={link.rel}
              href={getDomainBaseUrl() + link.href}
            />
          ))}
        </Helmet>
      </>
    );
  } else {
    // If no Meta from Drupal use a static set of meta from gatsby-config
    return (
      <Helmet
        htmlAttributes={{
          lang,
        }}
        title={title}
        titleTemplate={`%s | ${site?.siteMetadata?.title}`}
        meta={[
          {
            name: `description`,
            content: metaDescription,
          },
          {
            property: `og:title`,
            content: title,
          },
          {
            property: `og:description`,
            content: metaDescription,
          },
          {
            property: `og:type`,
            content: `website`,
          },
          {
            property: `og:image`,
            content: `${getDomainBaseUrl()}/${getDomain()}-fallback-image.jpeg`,
          },
          {
            name: `twitter:card`,
            content: `summary`,
          },
          {
            name: `twitter:title`,
            content: title,
          },
          {
            name: `twitter:description`,
            content: metaDescription,
          },
          {
            name: `twitter_cards_image`,
            content: `${getDomainBaseUrl()}/${getDomain()}-fallback-image.jpeg`,
          },
          {
            name: `HandheldFriendly`,
            content: `true`,
          },
          {
            name: `MobileOptimized`,
            content: `width`,
          },
        ]}
      />
    );
  }
};
